import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { feeScheduleTemplateApi } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuerySchema, { FeeScheduleTemplateQueryJSON } from "work/fee-schedule-templates/_api/queries/_schema/fee-schedule-template-query-schema";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import FeeScheduleTemplateSchema, {
  FeeScheduleTemplateJSON,
} from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

export type FeeScheduleTemplateLoadIssue = { id: Guid; issue: string };

export const isFeeScheduleTemplateLoadIssue = (value: object): value is FeeScheduleTemplateLoadIssue => {
  return "issue" in value;
};

export type FeeScheduleTemplateStoreState = {
  byId: Record<string, FeeScheduleTemplateJSON | null | undefined>;
  byQuery: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>;
  interfaceQueries: {
    entries: Record<string, FeeScheduleTemplateQueryJSON>;
    lastSuccessfulResponses: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>;
  };
};

const initialState: FeeScheduleTemplateStoreState = {
  byId: {},
  byQuery: {},
  interfaceQueries: {
    entries: {},
    lastSuccessfulResponses: {},
  },
};

const feeScheduleTemplatesSlice = createSlice({
  name: "feeScheduleTemplates",
  initialState,
  reducers: {
    addFeeScheduleTemplate: (state, action: PayloadAction<FeeScheduleTemplate>) => {
      const feeScheduleTemplate = action.payload;
      if (!feeScheduleTemplate.id) {
        console.error("Attempted to add fee schedule template without an id to the store");
        return;
      }
      state.byId[feeScheduleTemplate.id.value] = feeScheduleTemplate.toJSON();
      state.byQuery = {};
    },
    addFeeScheduleTemplateJSON: (state, action: PayloadAction<FeeScheduleTemplateJSON>) => {
      const feeScheduleTemplateJson = action.payload;
      if (!feeScheduleTemplateJson.id) {
        console.error("Attempted to add fee schedule template JSON without an id to the store");
        return;
      }
      state.byId[feeScheduleTemplateJson.id] = feeScheduleTemplateJson;
      state.byQuery = {};
    },
    removeFeeScheduleTemplate: (state, action: PayloadAction<Guid>) => {
      state.byId[action.payload.value] = null;
      state.byQuery = {};
    },
    updateFeeScheduleTemplateInterfaceQuery: (
      state,
      action: PayloadAction<{ interfaceName: string; query: FeeScheduleTemplateQuery }>
    ) => {
      const { interfaceName, query } = action.payload;

      const next = FeeScheduleTemplateQuerySchema.serialize(query);
      const prev = state.interfaceQueries.entries[interfaceName];

      if (prev && JSON.stringify(prev) === JSON.stringify(next)) return;

      state.interfaceQueries.entries[interfaceName] = next;
    },
    resetFeeScheduleTemplatesByQueryState: (state) => {
      state.byQuery = {};
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(feeScheduleTemplateApi.endpoints.getFeeScheduleTemplateById.matchFulfilled, (state, action) => {
      if (action.payload === null) {
        state.byId[action.meta.arg.originalArgs.feeScheduleTemplateId] = null;
        return;
      }
      const incomingFeeScheduleTemplateJSON: FeeScheduleTemplateJSON = action.payload;
      if (!incomingFeeScheduleTemplateJSON.id) {
        console.error("Received fee schedule template JSON without an id from getFeeScheduleTemplateById endpoint");
        return;
      }
      state.byId[incomingFeeScheduleTemplateJSON.id] = incomingFeeScheduleTemplateJSON;
    });

    builder.addMatcher(feeScheduleTemplateApi.endpoints.getFeeScheduleTemplatesByIds.matchFulfilled, (state, action) => {
      for (const feeScheduleTemplate of action.payload) {
        if (!feeScheduleTemplate.id) {
          console.error("Received fee schedule template JSON without an id from getFeeScheduleTemplatesByIds endpoint");
          continue;
        }
        state.byId[feeScheduleTemplate.id] = feeScheduleTemplate;
      }
    });

    builder.addMatcher(feeScheduleTemplateApi.endpoints.getFeeScheduleTemplates.matchFulfilled, (state, action) => {
       if (!action.meta.arg.originalArgs) return;
      const { query } = action.meta.arg.originalArgs;
      const responseJson = action.payload;
      if (!query || !responseJson) return;
      const queryKey = query.cacheKey;

      const validatedFeeScheduleTemplateData: (FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue)[] = [];

      for (const data of responseJson.data) {
        const feeScheduleTemplateJSON: FeeScheduleTemplateJSON = data;

        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(feeScheduleTemplateJSON.id) ?? id;
          if (FeeScheduleTemplateSchema.validateJSON(feeScheduleTemplateJSON)) {
            validatedFeeScheduleTemplateData.push(feeScheduleTemplateJSON);
          }
        } catch (error) {
          validatedFeeScheduleTemplateData.push({
            id,
            issue: "Error loading Fee Schedule Template.",
          });
          console.error("Error validating Fee Schedule Template JSON:", error);
        }
      }

      const paginatedResponse = PaginatedResponse.from<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>(
        validatedFeeScheduleTemplateData,
        responseJson.headers,
      );

      state.byQuery[queryKey] = paginatedResponse;

      for (const feeScheduleTemplateData of paginatedResponse.data) {
        if (!feeScheduleTemplateData.id) {
          console.error("Failed to deserialize incoming fee schedule template", feeScheduleTemplateData);
          continue;
        }
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateData)) continue;

        const incomingFeeScheduleTemplate: FeeScheduleTemplate | null | undefined = FeeScheduleTemplate.fromJSON(
          feeScheduleTemplateData
        );
        if (!incomingFeeScheduleTemplate?.id) {
          console.error("Failed to deserialize incoming fee schedule template", feeScheduleTemplateData);
          continue;
        }

        state.byId[incomingFeeScheduleTemplate.id.value] = feeScheduleTemplateData;
      }
    });

    builder.addMatcher(feeScheduleTemplateApi.endpoints.deleteFeeScheduleTemplate.matchFulfilled, (state, action) => {
      const templateIdValue = action.meta.arg.originalArgs.templateId;
      if (!templateIdValue) {
        console.error("Template does not have an id", templateIdValue);
        return;
      }
      state.byId[templateIdValue] = null;
    });

  }
});

export const {
  addFeeScheduleTemplate,
  addFeeScheduleTemplateJSON,
  removeFeeScheduleTemplate,
  updateFeeScheduleTemplateInterfaceQuery,
  resetFeeScheduleTemplatesByQueryState,
} = feeScheduleTemplatesSlice.actions;

const selectFeeScheduleTemplateIdValue = createSelector(
  [
    (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }, idValue: string | undefined) => state.feeScheduleTemplates.byId[idValue ?? ""],
    (_state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }, _idValue: string | undefined) => undefined,
  ],
  (feeScheduleTemplateEntryJSON: FeeScheduleTemplateJSON | null | undefined): FeeScheduleTemplateJSON | undefined => {
    if (!feeScheduleTemplateEntryJSON) return;
    return feeScheduleTemplateEntryJSON;
  },
);
export const getFeeScheduleTemplateById = (id: Guid | undefined) => (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) =>
  selectFeeScheduleTemplateIdValue(state, id?.value);

const selectFeeScheduleTemplateQueryByInterface = createSelector(
  [
    (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) => state.feeScheduleTemplates.interfaceQueries.entries,
    (_: { feeScheduleTemplates: FeeScheduleTemplateStoreState }, interfaceName: string) => interfaceName,
  ],
  (queries, interfaceName) => {
    const queryJson = queries[interfaceName];
    if (!queryJson) return;
    return FeeScheduleTemplateQuery.fromJSON(queryJson);
  },
);
export const getFeeScheduleTemplateQueryByInterface = (interfaceName: string) => (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) =>
  selectFeeScheduleTemplateQueryByInterface(state, interfaceName);

export const getFeeScheduleTemplatesByQuery = (query: FeeScheduleTemplateQuery | undefined) => (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) =>
  state.feeScheduleTemplates.byQuery[query?.cacheKey ?? ""];

export const getLastSuccessfulFeeScheduleTemplateQueryByInterface =
  (interfaceName: string) => (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) => {
    return state.feeScheduleTemplates.interfaceQueries.lastSuccessfulResponses[interfaceName];
  };

const selectFeeScheduleTemplatesByIds = createSelector(
  [
    (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) => state.feeScheduleTemplates.byId,
    (_: { feeScheduleTemplates: FeeScheduleTemplateStoreState }, ids: Guid[]) => ids,
  ],
  (entries, ids) => {
    return ids.map((id) => entries[id.value]).filter((feeScheduleTemplate) => feeScheduleTemplate !== undefined);
  },
);
export const getFeeScheduleTemplatesByIds = (ids: Guid[]) => (state: { feeScheduleTemplates: FeeScheduleTemplateStoreState }) => {
  return selectFeeScheduleTemplatesByIds(state, ids);
};

const selectLastSuccessfulResponsesEntries = (state: RootState) =>
  state.feeScheduleTemplates.interfaceQueries.lastSuccessfulResponses;
const selectLastSuccessfulFeeScheduleTemplatesResponseByInterface = createSelector(
  [selectLastSuccessfulResponsesEntries, (_: RootState, interfaceName: string) => interfaceName],
  (
    entries: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>,
    interfaceName: string
  ): PaginatedResponse<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> | undefined => {
    const pagedJson = entries[interfaceName];
    if (!pagedJson) return;

    const feeScheduleTemplateData = pagedJson.data
      .map((feeScheduleTemplateJson: FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue | undefined) => {
        if (!feeScheduleTemplateJson) return undefined;
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
          return feeScheduleTemplateJson;
        }
        return FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
      })
      .filter((n): n is FeeScheduleTemplate | FeeScheduleTemplateLoadIssue => typeof n === "object" && n !== null);
    return PaginatedResponse.from<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>(
      feeScheduleTemplateData,
      pagedJson.headers
    );
  }
);
export const getLastSuccessfulFeeScheduleTemplateResponseByInterface =
  (interfaceName: string) => (state: RootState) => {
    return selectLastSuccessfulFeeScheduleTemplatesResponseByInterface(state, interfaceName);
  };

export default feeScheduleTemplatesSlice;
